/**
 * Error Configuration
 * Centralized configuration for error handling, reporting, and messages
 */

export const ERROR_CONFIG = {
  // Error reporting settings
  REPORTING: {
    ENABLED: process.env.NODE_ENV === 'production',
    BATCH_SIZE: 10,
    FLUSH_INTERVAL: 30000, // 30 seconds
    MAX_RETRIES: 3,
    TIMEOUT: 10000, // 10 seconds
  },

  // Error severity levels
  SEVERITY: {
    LOW: 'low',
    MEDIUM: 'medium',
    HIGH: 'high',
    CRITICAL: 'critical',
  },

  // Error categories
  CATEGORIES: {
    AUTHENTICATION: 'authentication',
    AUTHORIZATION: 'authorization',
    VALIDATION: 'validation',
    NETWORK: 'network',
    FILE_UPLOAD: 'file_upload',
    DATABASE: 'database',
    AI_SERVICE: 'ai_service',
    UI_COMPONENT: 'ui_component',
    UNKNOWN: 'unknown',
  },

  // Error messages
  MESSAGES: {
    // Authentication errors
    AUTH_REQUIRED: 'Authentication required to access this resource',
    AUTH_FAILED: 'Authentication failed. Please check your credentials.',
    AUTH_EXPIRED: 'Your session has expired. Please sign in again.',
    
    // Authorization errors
    ACCESS_DENIED: 'You do not have permission to access this resource',
    INSUFFICIENT_PRIVILEGES: 'Insufficient privileges to perform this action',
    
    // Validation errors
    VALIDATION_FAILED: 'Validation failed. Please check your input.',
    REQUIRED_FIELD_MISSING: 'Required field is missing',
    INVALID_FORMAT: 'Invalid data format',
    
    // Network errors
    NETWORK_ERROR: 'Network error. Please check your connection.',
    SERVER_ERROR: 'Server error. Please try again later.',
    REQUEST_TIMEOUT: 'Request timed out. Please try again.',
    
    // File upload errors
    FILE_TOO_LARGE: 'File size exceeds maximum allowed limit',
    INVALID_FILE_TYPE: 'Invalid file type',
    UPLOAD_FAILED: 'File upload failed. Please try again.',
    NO_FILES_SELECTED: 'No files selected for upload',
    MISSING_FILE_PAIR: 'All files must have both .wav and .mp3 pairs',
    
    // Database errors
    SAVE_FAILED: 'Failed to save data. Please try again.',
    LOAD_FAILED: 'Failed to load data. Please refresh the page.',
    DELETE_FAILED: 'Failed to delete item. Please try again.',
    
    // AI service errors
    AI_SERVICE_UNAVAILABLE: 'AI service is currently unavailable',
    AI_REQUEST_FAILED: 'AI request failed. Please try again.',
    AI_RESPONSE_INVALID: 'Invalid response from AI service',
    
    // UI component errors
    COMPONENT_LOAD_FAILED: 'Failed to load component',
    COMPONENT_CRASHED: 'Component encountered an error',
    
    // Generic errors
    UNEXPECTED_ERROR: 'An unexpected error occurred',
    UNKNOWN_ERROR: 'Unknown error occurred',
    TRY_AGAIN: 'Please try again',
    CONTACT_SUPPORT: 'If the problem persists, please contact support',
  },

  // User-friendly error messages (for display)
  USER_MESSAGES: {
    GENERAL_ERROR: 'Something went wrong. Please try again.',
    NETWORK_ISSUE: 'Please check your internet connection and try again.',
    SERVICE_UNAVAILABLE: 'This service is temporarily unavailable. Please try again later.',
    PERMISSION_DENIED: 'You don\'t have permission to perform this action.',
    SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
    
    // File upload specific
    FILE_UPLOAD_ERROR: 'There was a problem uploading your file. Please try again.',
    FILE_SIZE_ERROR: 'Your file is too large. Please choose a smaller file.',
    FILE_TYPE_ERROR: 'This file type is not supported.',
    
    // Form specific
    FORM_SAVE_ERROR: 'Unable to save your changes. Please try again.',
    FORM_VALIDATION_ERROR: 'Please check the highlighted fields and try again.',
    
    // AI specific
    AI_PROCESSING_ERROR: 'Unable to process your request with AI. Please try again.',
  },

  // Error recovery suggestions
  RECOVERY_SUGGESTIONS: {
    REFRESH_PAGE: 'Try refreshing the page',
    CHECK_CONNECTION: 'Check your internet connection',
    RETRY_ACTION: 'Retry the action',
    CONTACT_ADMIN: 'Contact your administrator',
    CLEAR_CACHE: 'Clear your browser cache',
    TRY_DIFFERENT_FILE: 'Try a different file',
    REDUCE_FILE_SIZE: 'Reduce the file size',
  },

  // Error boundary configuration
  BOUNDARY: {
    FALLBACK_COMPONENT: 'ErrorFallback',
    LOG_ERRORS: true,
    REPORT_ERRORS: true,
    SHOW_DETAILS_IN_DEV: true,
  },

  // Retry configuration
  RETRY: {
    MAX_ATTEMPTS: 3,
    BASE_DELAY: 1000, // 1 second
    MAX_DELAY: 10000, // 10 seconds
    EXPONENTIAL_BACKOFF: true,
  },

  // Toast notification settings for errors
  TOAST: {
    DURATION: 5000, // 5 seconds
    AUTO_DISMISS: true,
    SHOW_RETRY_BUTTON: true,
    POSITION: 'top-right',
  },
};

export default ERROR_CONFIG;